// ─────────────────────────────────────────────────────────────────────
// <SntLogo />
//
// Renders the brand mark + wordmark.  Reads from /public/brand/ so the
// user can swap files without touching code.
//
//   variant="mark"       — just the butterfly+N glyph (default)
//   variant="wordmark"   — just the "Showz N Ticketz." typography
//   variant="full"       — mark + wordmark side by side (used in TopNav)
//
//   size  = number (px)  — height of the mark; wordmark scales proportional
//   themed = boolean     — if true, accent follows active theme; if false,
//                          uses the colors baked into /brand/logo-mark.svg
//
//   onClick               — optional click handler (e.g. nav to home)
// ─────────────────────────────────────────────────────────────────────

import { Link } from 'react-router-dom';
import { useThemeOptional } from '../../themes/ThemeContext';

const MARK_SRC = '/brand/logo-mark.svg';

// ─── Inline SVG (themed mode) ──────────────────────────────────────────
// When `themed=true`, we render the SVG inline so the right wing
// inherits the active theme accent via `currentColor`.  This is a copy
// of /public/brand/logo-mark.svg but with the gold fill swapped for
// `currentColor` and the wrapper's `color: var(--snt-accent)`.
function ThemedSvg({ size = 38 }) {
  const w = (size * 200) / 160;
  return (
    <svg
      width={w}
      height={size}
      viewBox="0 0 200 160"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Showz N Ticketz"
      style={{ color: 'var(--snt-accent)' }}
    >
      <path
        d="M100 80 C 88 50, 60 28, 32 36 C 8 44, 4 78, 14 100 C 24 122, 56 130, 80 116 C 92 109, 98 95, 100 82 Z"
        fill="var(--snt-text, #F5F5F4)"
        opacity="0.96"
      />
      <path
        d="M100 80 C 112 50, 140 28, 168 36 C 192 44, 196 78, 186 100 C 176 122, 144 130, 120 116 C 108 109, 102 95, 100 82 Z"
        fill="currentColor"
      />
      <line
        x1="100" y1="34" x2="100" y2="124"
        stroke="var(--snt-bg, #0A0A0B)"
        strokeWidth="2.5" strokeLinecap="round" opacity="0.85"
      />
      <circle cx="100" cy="32" r="5" fill="currentColor" />
      <circle cx="100" cy="80" r="13" fill="var(--snt-ruby, #B91C1C)" />
      <text
        x="100" y="86" textAnchor="middle"
        fill="var(--snt-text, #F5F5F4)" fontSize="16"
        fontFamily="Georgia, serif" fontStyle="italic" fontWeight="600"
      >
        N
      </text>
    </svg>
  );
}

// ─── Fallback img mode (uses user-replaced /brand/logo-mark.svg) ──────
function StaticImg({ size = 38 }) {
  const w = (size * 200) / 160;
  return (
    <img
      src={MARK_SRC}
      alt="Showz N Ticketz"
      width={w}
      height={size}
      style={{ display: 'block' }}
    />
  );
}

// ─── Wordmark ─────────────────────────────────────────────────────────
function Wordmark({ size = 19 }) {
  return (
    <span
      style={{
        fontFamily: 'var(--font-serif, "Fraunces")',
        fontSize: `${size}px`,
        letterSpacing: '-0.005em',
        lineHeight: 1.05,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ color: 'var(--snt-text, #F5F5F4)' }}>Showz</span>
      <span
        style={{
          color: 'var(--snt-accent, #D4A574)',
          fontStyle: 'italic',
          margin: '0 0.3em',
        }}
      >
        N
      </span>
      <span style={{ color: 'var(--snt-text, #F5F5F4)' }}>Ticketz</span>
      <span style={{ color: 'var(--snt-accent, #D4A574)' }}>.</span>
    </span>
  );
}

// ─── Public component ─────────────────────────────────────────────────
export default function SntLogo({
  variant = 'mark',     // 'mark' | 'wordmark' | 'full'
  size = 38,
  themed = true,
  to = '/',
  onClick,
  className = '',
  wordmarkSize,
}) {
  // r7.8: the optional read (no provider in tests) — a hook in a try/catch is a hook behind a
  // conditional, which react-hooks/rules-of-hooks rightly refuses.
  const _ctx = useThemeOptional();

  const wmSize = wordmarkSize || Math.round(size * 0.5);

  const inner = (
    <span className={`inline-flex items-center gap-3 ${className}`} style={{ lineHeight: 0 }}>
      {(variant === 'mark' || variant === 'full') && (
        themed ? <ThemedSvg size={size} /> : <StaticImg size={size} />
      )}
      {(variant === 'wordmark' || variant === 'full') && (
        <Wordmark size={wmSize} />
      )}
    </span>
  );

  if (!to) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="bg-transparent border-0 p-0 cursor-pointer"
      >
        {inner}
      </button>
    );
  }

  return (
    <Link
      to={to}
      onClick={onClick}
      className="no-underline"
      aria-label="Showz N Ticketz home"
    >
      {inner}
    </Link>
  );
}

// Re-export sub-pieces for advanced uses (splash, marketing kit, etc.)
SntLogo.Mark = ({ size, themed = true }) =>
  themed ? <ThemedSvg size={size} /> : <StaticImg size={size} />;
SntLogo.Wordmark = Wordmark;
